/**
 * `useMenu` — TanStack Query hook that resolves a WordPress nav-menu
 * tree by its registered theme location through the typed
 * `api/menus` module.
 *
 * The hook is a thin wrapper: it composes `getMenu` with the
 * deterministic query key produced by `hooks/keys.ts` and defers every
 * caching decision (`staleTime`, `gcTime`, retry policy, network mode)
 * to the shared `QueryClient` instance installed at the app root by
 * `providers/AppProviders.tsx`. Consumers therefore see the same
 * background-revalidation and retry semantics as the rest of the
 * app without this file re-declaring them (Req 5.1, 5.2).
 *
 * All HTTP traffic flows through `api/menus` → `api/client.ts`; the
 * hook itself never imports `axios`, so replacing the transport is a
 * one-file change in `api/client.ts`.
 *
 * The query is disabled while `location` is empty so callers can pass
 * the current theme-location slug directly — the hook will not fire a
 * request against an unresolved argument. Sanitisation of the slug
 * itself lives in `api/menus.ts` (Req 2.3).
 *
 * Returned value:
 *   - The raw `UseQueryResult<Menu>` from TanStack Query so callers
 *     can pattern-match on `isLoading`, `error`, `data`, and
 *     `refetch` per the design's state-management conventions.
 *
 * Requirements: 5.1, 5.2, 2.3.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getMenu } from '@/api/menus';
import type { Menu } from '@/types';

import { keys } from './keys';

/**
 * Subscribe to a nav-menu tree by its registered theme location.
 *
 * @param location - Theme-location slug identifying the nav menu on
 *   the WordPress side (e.g. `'primary'`, `'footer'`). An empty
 *   string keeps the query disabled.
 * @returns The raw `UseQueryResult` so consumers can access `data`,
 *          `isLoading`, `error`, `refetch`, etc.
 */
export function useMenu(location: string): UseQueryResult<Menu> {
  return useQuery({
    queryKey: keys.menu(location),
    queryFn: () => getMenu(location),
    enabled: location.length > 0,
  });
}
